'use client';

import { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

export interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  originalPrice?: number;
  image: string;
  alt: string;
  weight: string;
  availability: 'in-stock' | 'low-stock' | 'out-of-stock' | 'pre-order';
  stockCount: number;
  catchDate: string;
  rating: number;
  reviewCount: number;
  freshness: number;
  badges: string[];
  nutritionHighlights: string[];
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (productId: string, quantity: number) => void;
}

const ProductCard = ({ product, onAddToCart }: ProductCardProps) => {
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddToCart = () => {
    setIsAdding(true);
    onAddToCart(product.id, quantity);
    setTimeout(() => {
      setIsAdding(false);
      setQuantity(1);
    }, 1000);
  };

  const getAvailabilityBadge = () => {
    switch (product.availability) {
      case 'in-stock':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-xs font-medium">
            <Icon name="CheckCircleIcon" size={14} />
            In Stock
          </span>
        );
      case 'low-stock':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-md text-xs font-medium">
            <Icon name="ExclamationTriangleIcon" size={14} />
            Low Stock ({product.stockCount})
          </span>
        );
      case 'out-of-stock':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-error/10 text-error rounded-md text-xs font-medium">
            <Icon name="XCircleIcon" size={14} />
            Out of Stock
          </span>
        );
      case 'pre-order':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent rounded-md text-xs font-medium">
            <Icon name="ClockIcon" size={14} />
            Pre-Order
          </span>
        );
    }
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <div className="group bg-card border border-border rounded-md overflow-hidden shadow-elevation-sm hover:shadow-elevation-md transition-smooth">
      {/* Image Section */}
      <Link href={`/product-details/${product.id}`} className="block relative">
        <div className="relative w-full h-48 overflow-hidden bg-muted">
          <AppImage
            src={product.image}
            alt={product.alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
          />
          
          {/* Badges Overlay */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {discount > 0 && (
              <span className="px-2 py-1 bg-error text-error-foreground rounded-md text-xs font-bold">
                {discount}% OFF
              </span>
            )}
            {product.badges.map((badge, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-primary text-primary-foreground rounded-md text-xs font-medium"
              >
                {badge}
              </span>
            ))}
          </div>

          {/* Freshness Indicator */}
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-card/90 backdrop-blur-sm rounded-md">
            <Icon name="SparklesIcon" size={14} className="text-success" />
            <span className="font-data text-xs font-medium text-foreground">
              {product.freshness}% Fresh
            </span>
          </div>
        </div>
      </Link>

      {/* Content Section */}
      <div className="p-4 space-y-3">
        {/* Category & Rating */}
        <div className="flex items-center justify-between">
          <span className="font-caption text-xs text-muted-foreground uppercase tracking-wide">
            {product.category}
          </span>
          <div className="flex items-center gap-1">
            <Icon name="StarIcon" size={14} className="text-warning" variant="solid" />
            <span className="font-data text-xs font-medium text-foreground">
              {product.rating.toFixed(1)}
            </span>
            <span className="font-caption text-xs text-muted-foreground">
              ({product.reviewCount})
            </span>
          </div>
        </div>

        {/* Product Name */}
        <Link href={`/product-details/${product.id}`}>
          <h3 className="font-heading font-semibold text-base text-foreground hover:text-primary transition-smooth line-clamp-2">
            {product.name}
          </h3>
        </Link>

        {/* Weight & Catch Date */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Icon name="ScaleIcon" size={14} />
            <span>{product.weight}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="CalendarIcon" size={14} />
            <span>Caught: {product.catchDate}</span>
          </div>
        </div>

        {/* Nutrition Highlights */}
        <div className="flex flex-wrap gap-1.5">
          {product.nutritionHighlights.slice(0, 3).map((highlight, index) => (
            <span
              key={index}
              className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs"
            >
              {highlight}
            </span>
          ))}
        </div>

        {/* Price Section */}
        <div className="flex items-end gap-2">
          <span className="font-heading font-bold text-2xl text-primary">
            ₹{product.price.toFixed(2)}
          </span>
          {product.originalPrice && (
            <span className="font-data text-sm text-muted-foreground line-through">
              ₹{product.originalPrice.toFixed(2)}
            </span>
          )}
        </div>

        {/* Availability Badge */}
        <div>{getAvailabilityBadge()}</div>

        {/* Add to Cart Section */}
        {product.availability !== 'out-of-stock' && (
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <div className="flex items-center border border-border rounded-md overflow-hidden">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="px-3 py-2 hover:bg-muted transition-smooth"
                aria-label="Decrease quantity"
              >
                <Icon name="MinusIcon" size={16} />
              </button>
              <span className="px-4 py-2 font-data font-medium text-sm border-x border-border">
                {quantity}
              </span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="px-3 py-2 hover:bg-muted transition-smooth"
                aria-label="Increase quantity"
              >
                <Icon name="PlusIcon" size={16} />
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAdding ? (
                <>
                  <Icon name="CheckIcon" size={18} />
                  <span>Added!</span>
                </>
              ) : (
                <>
                  <Icon name="ShoppingCartIcon" size={18} />
                  <span>Add to Cart</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;